import { Link } from "react-router-dom";
import { SkillBadge } from "../SkillBadge";
import { Trash2, Circle, Phone, Slash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Candidate } from "@/types/candidate";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useCandidateStatus } from "@/utils/candidate/statusUtils";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CLOSED_MATCH_STATUSES_FILTER } from "@/utils/matches/status";

interface ListMemberCardProps {
  candidate: Candidate;
  className?: string;
  onRemove?: (candidateId: string) => void;
}

export const ListMemberCard = ({ candidate, className, onRemove }: ListMemberCardProps) => {
  const { t } = useTranslation();
  const { getStatusColor, getLocalizedStatus } = useCandidateStatus();
  const skillsContainerRef = useRef<HTMLDivElement>(null);
  const [maxVisibleSkills, setMaxVisibleSkills] = useState<number>(4);
  const [matchCount, setMatchCount] = useState<number>(0);

  // Fetch the actual match count for this candidate, excluding closed matches
  useEffect(() => {
    const fetchMatchCount = async () => {
      try {
        const { count, error } = await supabase
          .from('matches')
          .select('id', { count: 'exact', head: false })
          .eq('candidate_id', candidate.id)
          .not('status', 'in', CLOSED_MATCH_STATUSES_FILTER);
          
        if (error) {
          console.error("Error fetching match count:", error);
          return;
        }
        
        setMatchCount(count || 0);
      } catch (err) {
        console.error("Failed to fetch match count:", err);
      }
    };
    
    fetchMatchCount();
  }, [candidate.id]);

  const handleRemove = () => {
    if (onRemove) {
      onRemove(candidate.id);
    }
  };

  const lowercaseStatus = candidate.status?.toLowerCase();

  return (
    <div className={cn(
      "relative flex flex-col h-full overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-sm bg-white/80 border border-gray-100 rounded-sm",
      "hover:bg-white/90 hover:scale-[1.02] hover:border-gray-200 hover:origin-center",
      "dark:bg-gray-800/80 dark:border-gray-700/20 dark:hover:bg-gray-800/90 dark:hover:border-gray-700/30",
      className,
      "w-full h-full"
    )}>
      <div className="p-4 border-b border-border flex flex-row justify-between items-start min-h-[60px]">
        <div className="flex-1 min-w-0">
          <Link 
            to={`/candidates/${candidate.id}`} 
            className="hover:text-primary transition-colors"
          >
            <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100">{candidate.name}</h3>
          </Link>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRemove}
          className="text-red-600/30 hover:text-red-800/50 hover:bg-red-50/50 ml-2"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 flex flex-col justify-between p-4 gap-2 pb-0">
        <div className="flex flex-col gap-1 min-h-[40px]">
          <span className="text-sm text-gray-500 dark:text-gray-400">{candidate.function || <span>&nbsp;</span>}</span>
        </div>
        <div className="flex flex-col gap-1 min-h-[48px]">
          <span className="text-sm text-gray-500 dark:text-gray-400">Email: {candidate.email ? <a href={`mailto:${candidate.email}`} className="text-blue-500 hover:underline dark:text-blue-400">{candidate.email}</a> : <span className="text-gray-400 dark:text-gray-500">Not provided</span>}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Phone: {candidate.phone ? <a href={`tel:${candidate.phone}`} className="text-blue-500 hover:underline dark:text-blue-400">{candidate.phone}</a> : <span className="text-gray-400 dark:text-gray-500">Not provided</span>}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2 min-h-[24px]">
          {candidate.skills && candidate.skills.length > 0 ? (
            <>
              {candidate.skills.slice(0, 3).map((skill) => (
                <SkillBadge key={skill} label={skill} className="text-xs py-1 px-2 h-6" />
              ))}
              {candidate.skills.length > 3 && (
                <SkillBadge
                  key="more-badge"
                  label={`+${candidate.skills.length - 3} more`}
                  className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-xs py-1 px-2 h-6"
                />
              )}
            </>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">No skills</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className={cn("text-xs flex items-center gap-1", getStatusColor(candidate.status))}>
            <Circle className="h-3 w-3" />
            {getLocalizedStatus(candidate.status)}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Matches:</span>
          <span className="font-medium text-blue-500 dark:text-blue-400">{matchCount}</span>
        </div>
      </div>
      <div className="flex justify-end items-end p-4 pt-2">
        <Link
          to={`/candidates/${candidate.id}`}
          className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-sm bg-blue-500 text-white hover:bg-blue-500 transition-colors dark:bg-blue-500 dark:hover:bg-blue-500"
        >
          {t("contacts.view")}
        </Link>
      </div>
    </div>
  );
};
